import React, { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useLocale } from '../../contexts/LocaleContext';
import { LanguageSelector } from '../LanguageSelector';
import type { AuthError } from '../../types/auth';
import login_styles from '../LoginPage/LoginPage.module.css';
import styles from './AdminLoginPage.module.css';

interface AdminLoginPageProps {
  onSuccess?: () => void;
}

const AdminLoginPage: React.FC<AdminLoginPageProps> = ({ onSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const { signInAsAdmin } = useAuth();
  const { selectedLocale } = useLocale();

  // Translation helper for dynamic content
  const t = (en: string, ru: string) => {
    return selectedLocale === 'ru' ? ru : en;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await signInAsAdmin(username, password);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const authError = err as AuthError;

      if (authError.status === 401) {
        setError(
          t(
            'Invalid administrator credentials. Please try again.',
            'Неверные данные администратора. Попробуйте снова.'
          )
        );
      } else if (authError.status === 500) {
        setError(t('Server error. Please try again later.', 'Ошибка сервера. Попробуйте позже.'));
      } else {
        setError(
          authError.message || t('Login failed. Please try again.', 'Ошибка входа. Попробуйте снова.')
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={login_styles.login_container}>
      <div className={login_styles.login_card}>
        {/* Three-column header: Language Selector, Logo, Spacer */}
        <div className={login_styles.card_header}>
          <div className={login_styles.header_left}>
            <LanguageSelector />
          </div>
          <div className={login_styles.header_center}>
            <img
              src="/diana_logo_monochrome.png"
              alt="Diana Lapteva"
              className={login_styles.logo}
            />
          </div>
          <div className={login_styles.header_right}>
            {/* Spacer for symmetry */}
          </div>
        </div>

        {/* Title section */}
        <div className={login_styles.login_header}>
          <span className={styles.admin_badge}>{t('Admin', 'Админ')}</span>
          <h1 className={styles.admin_title}>{t('Admin Panel', 'Панель администратора')}</h1>
          <p className={styles.admin_subtitle}>
            {t(
              'Sign in with your administrator account',
              'Войдите с учётной записью администратора'
            )}
          </p>
        </div>

        {/* Login form */}
        <form onSubmit={handleSubmit} className={login_styles.login_form}>
          <div className={login_styles.form_group}>
            <label htmlFor="admin-username" className={login_styles.form_label}>
              {t('Username', 'Логин')}
            </label>
            <input
              id="admin-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={login_styles.form_input}
              placeholder={t('Enter your username', 'Введите ваш логин')}
              autoComplete="username"
              autoFocus
              required
              disabled={isLoading}
              aria-describedby={error ? 'admin-error-message' : undefined}
            />
          </div>

          <div className={login_styles.form_group}>
            <label htmlFor="admin-password" className={login_styles.form_label}>
              {t('Password', 'Пароль')}
            </label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={login_styles.form_input}
              placeholder={t('Enter your password', 'Введите ваш пароль')}
              autoComplete="current-password"
              required
              disabled={isLoading}
              aria-describedby={error ? 'admin-error-message' : undefined}
            />
          </div>

          {/* Error message */}
          {error && (
            <div id="admin-error-message" className={login_styles.error_message} role="alert">
              {error}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            className={login_styles.submit_button}
            disabled={!username.trim() || !password.trim() || isLoading}
          >
            {isLoading ? (
              <span className={login_styles.loading}>
                <span className={login_styles.spinner} aria-hidden="true"></span>
                {t('Loading...', 'Загрузка...')}
              </span>
            ) : (
              t('SIGN IN', 'ВОЙТИ')
            )}
          </button>
        </form>

        {/* Footer */}
        <div className={login_styles.login_footer}>
          <div className={login_styles.footer_divider}></div>
          <p className={login_styles.footer_text}>
            {t('Authorised personnel only', 'Только для авторизованного персонала')}
          </p>
          <Link to="/" className={styles.back_link}>
            ← {t('Back to the main website', 'На главный сайт')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
